import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Link,
} from '@mui/material';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useNotifications } from '../../hooks/useNotifications';
import { forgotPassword } from '../../api/auth';
import { forgotPasswordStyles } from './ForgotPassword.styles';
import { t } from '../../utils/translations';

const ForgotPasswordForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { showError } = useNotifications();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      showError(t('forgotPassword.emailRequired'), t('common.error'));
      return;
    }

    if (!executeRecaptcha) {
      showError(t('auth.recaptchaFailed'), t('common.error'));
      return;
    }

    setIsLoading(true);

    try {
      const recaptchaToken = await executeRecaptcha('forgot_password');

      await forgotPassword({ email, recaptchaToken });
      setEmailSent(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('forgotPassword.requestFailed');
      showError(errorMessage, t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={forgotPasswordStyles.container}>
      <Paper elevation={3} sx={forgotPasswordStyles.paper}>
        <Box sx={forgotPasswordStyles.content}>
          <Typography variant="h4" component="h1" color="primary" sx={forgotPasswordStyles.title}>
            Listum
          </Typography>

          {emailSent ? (
            <Box sx={forgotPasswordStyles.successMessage}>
              <Typography variant="h6" sx={forgotPasswordStyles.successTitle}>
                {t('forgotPassword.emailSentTitle')}
              </Typography>
              <Typography variant="body1" sx={forgotPasswordStyles.successDescription}>
                {t('forgotPassword.emailSentDescription')}
              </Typography>
              <Box sx={forgotPasswordStyles.backToLoginLink}>
                <Link href="/" underline="hover">
                  {t('forgotPassword.backToLogin')}
                </Link>
              </Box>
            </Box>
          ) : (
            <>
              <Typography variant="h5" component="h2" sx={forgotPasswordStyles.subtitle}>
                {t('forgotPassword.title')}
              </Typography>

              <Typography variant="body2" sx={forgotPasswordStyles.description}>
                {t('forgotPassword.description')}
              </Typography>

              <Box component="form" onSubmit={handleSubmit} sx={forgotPasswordStyles.form}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  sx={forgotPasswordStyles.input}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isLoading}
                  sx={forgotPasswordStyles.submitButton}
                >
                  {isLoading ? <CircularProgress size={24} /> : t('forgotPassword.submitButton')}
                </Button>

                <Box sx={forgotPasswordStyles.backToLoginLink}>
                  <Link href="/" underline="hover">
                    {t('forgotPassword.backToLogin')}
                  </Link>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

const ForgotPassword: React.FC = () => {
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY;

  if (!recaptchaSiteKey) {
    return (
      <Container>
        <Typography color="error">
          {t('auth.recaptchaNotConfigured')}
        </Typography>
      </Container>
    );
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
      <ForgotPasswordForm />
    </GoogleReCaptchaProvider>
  );
};

export default ForgotPassword;
